import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, ThumbsUp, Share2, Volume2, ChevronsUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function mediaUrl(u) {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

function handleFromName(name) {
  if (!name) return "alchemistca";
  // "Alchemist (CA)" → "alchemistca"; spaces + punctuation stripped.
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function initialsFromName(name) {
  if (!name) return "AC";
  const parts = name.replace(/\(.*\)/g, "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || "A").slice(0, 2).toUpperCase();
}

function Avatar({ size = 32, initials }) {
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#FF3B00] to-[#7a1f00] flex items-center justify-center font-mono text-white shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
}

function MediaEl({ draft, className = "" }) {
  const videoSrc = mediaUrl(draft.video_url);
  const imageSrc = mediaUrl(draft.image_url);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  if (videoSrc) {
    const togglePlay = (e) => {
      e.stopPropagation();
      e.preventDefault();
      const v = videoRef.current;
      if (!v) return;
      if (v.paused) { v.play().catch(() => {}); }
      else { v.pause(); }
    };
    return (
      <div className={`relative bg-black ${className}`} onClick={togglePlay} data-testid="preview-video-wrap">
        <video
          ref={videoRef}
          src={videoSrc}
          poster={imageSrc || undefined}
          controls
          playsInline
          preload="metadata"
          className="w-full h-full object-cover pointer-events-none"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          data-testid="preview-video"
        />
        {!isPlaying && (
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/45 transition-colors group z-20"
            data-testid="preview-video-play"
            aria-label="Play video"
          >
            <span className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform">
              <span className="ml-1 w-0 h-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-black" />
            </span>
          </button>
        )}
      </div>
    );
  }
  if (imageSrc) {
    return <img src={imageSrc} alt="" className={`bg-black ${className}`} />;
  }
  return (
    <div className={`bg-black flex items-center justify-center text-white/30 label-mono ${className}`}>
      No visual
    </div>
  );
}

function InstagramReelMock({ draft, artist }) {
  const caption = draft.caption || "";
  const hashtags = (draft.hashtags || []).join(" ");
  return (
    <div className="mx-auto w-[330px] h-[680px] bg-black rounded-[32px] border-[6px] border-neutral-800 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0">
        <MediaEl draft={draft} className="w-full h-full object-cover" />
      </div>
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-4 pt-3 flex items-center justify-between text-white z-10">
        <div className="font-semibold text-[15px]">Reels</div>
        <div className="w-6 h-6 rounded-full bg-white/10" />
      </div>
      {/* Right rail */}
      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-5 text-white z-10">
        <div className="flex flex-col items-center"><Heart size={26} strokeWidth={2} fill="white" /><span className="text-[11px] font-mono mt-1">12.4k</span></div>
        <div className="flex flex-col items-center"><MessageCircle size={26} strokeWidth={2} /><span className="text-[11px] font-mono mt-1">248</span></div>
        <div className="flex flex-col items-center"><Send size={24} strokeWidth={2} /><span className="text-[11px] font-mono mt-1">Share</span></div>
        <div className="flex flex-col items-center"><Bookmark size={24} strokeWidth={2} /></div>
        <div className="flex flex-col items-center"><MoreHorizontal size={24} strokeWidth={2} /></div>
      </div>
      {/* Bottom caption */}
      <div className="absolute left-0 right-16 bottom-0 p-4 text-white z-10"
           style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.85) 100%)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Avatar size={30} initials={artist.initials} />
          <div className="font-semibold text-[14px]">{artist.handle}</div>
          <div className="text-xs border border-white/70 rounded-sm px-2 py-[1px]">Follow</div>
        </div>
        <div className="text-[13px] leading-snug line-clamp-3" style={{ whiteSpace: "pre-wrap" }}>
          {caption}
          {hashtags && <span className="text-[#b7d6ff]"> {hashtags}</span>}
        </div>
        <div className="flex items-center gap-2 mt-3 text-[12px]">
          <Volume2 size={14} />
          <span className="font-mono truncate">{draft.audio_filename ? `♪ ${draft.audio_filename}` : `♪ Original audio · ${artist.handle}`}</span>
        </div>
      </div>
    </div>
  );
}

function FacebookPostMock({ draft, artist }) {
  const caption = draft.caption || "";
  return (
    <div className="mx-auto w-[500px] bg-[#242526] rounded-lg border border-white/5 shadow-2xl text-white overflow-hidden">
      <div className="p-4 flex items-center gap-3">
        <Avatar size={40} initials={artist.initials} />
        <div className="flex-1">
          <div className="font-semibold text-[15px]">{artist.name}</div>
          <div className="text-[12px] text-white/50">Just now · <span className="font-mono">🌎</span></div>
        </div>
        <MoreHorizontal size={20} className="text-white/60" />
      </div>
      <div className="px-4 pb-3 text-[14px] leading-relaxed whitespace-pre-wrap">{caption}</div>
      <MediaEl draft={draft} className="w-full max-h-[500px] object-cover" />
      <div className="px-4 py-2 flex items-center justify-between text-[12px] text-white/60 border-t border-white/5">
        <div className="flex items-center gap-1">
          <span className="w-5 h-5 rounded-full bg-[#1877F2] flex items-center justify-center text-[10px]">👍</span>
          <span>{(842).toLocaleString()}</span>
        </div>
        <div>42 comments · 18 shares</div>
      </div>
      <div className="grid grid-cols-3 text-[14px] font-medium text-white/80 border-t border-white/5">
        <button className="py-2 flex items-center justify-center gap-2 hover:bg-white/5"><ThumbsUp size={18} /> Like</button>
        <button className="py-2 flex items-center justify-center gap-2 hover:bg-white/5"><MessageCircle size={18} /> Comment</button>
        <button className="py-2 flex items-center justify-center gap-2 hover:bg-white/5"><Share2 size={18} /> Share</button>
      </div>
    </div>
  );
}

function YoutubeShortMock({ draft, artist }) {
  const titleLine = (draft.caption || "").split("\n")[0].slice(0, 100);
  const body = (draft.caption || "").split("\n").slice(1).join("\n").trim() || (draft.hashtags || []).join(" ");
  return (
    <div className="mx-auto w-[330px] h-[680px] bg-black rounded-[32px] border-[6px] border-neutral-800 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0">
        <MediaEl draft={draft} className="w-full h-full object-cover" />
      </div>
      {/* Right rail */}
      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-6 text-white z-10">
        <div className="flex flex-col items-center"><ThumbsUp size={24} strokeWidth={2} /><span className="text-[11px] font-mono mt-1">3.2k</span></div>
        <div className="flex flex-col items-center"><ThumbsUp size={24} strokeWidth={2} className="rotate-180" /><span className="text-[11px] font-mono mt-1">Dislike</span></div>
        <div className="flex flex-col items-center"><MessageCircle size={24} strokeWidth={2} /><span className="text-[11px] font-mono mt-1">128</span></div>
        <div className="flex flex-col items-center"><Share2 size={24} strokeWidth={2} /><span className="text-[11px] font-mono mt-1">Share</span></div>
        <div className="flex flex-col items-center"><ChevronsUp size={24} strokeWidth={2} /><span className="text-[11px] font-mono mt-1">Remix</span></div>
      </div>
      {/* Bottom caption */}
      <div className="absolute left-0 right-16 bottom-0 p-4 text-white z-10"
           style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.85) 100%)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Avatar size={30} initials={artist.initials} />
          <div className="font-semibold text-[14px]">@{artist.handle}</div>
          <div className="text-xs bg-white text-black rounded-full px-3 py-[2px] font-semibold">Subscribe</div>
        </div>
        <div className="font-semibold text-[14px] leading-snug mb-1 line-clamp-2">{titleLine} #Shorts</div>
        {body && (
          <div className="text-[12px] text-white/80 leading-snug line-clamp-2" style={{ whiteSpace: "pre-wrap" }}>{body}</div>
        )}
        <div className="flex items-center gap-2 mt-2 text-[12px] text-white/80">
          <Volume2 size={13} />
          <span className="font-mono truncate">{draft.audio_filename ? `♪ ${draft.audio_filename}` : `♪ ${artist.name} · Original audio`}</span>
        </div>
      </div>
    </div>
  );
}

export default function DraftPreviewDialog({ draft, open, onOpenChange }) {
  const { user } = useAuth();
  const [view, setView] = useState(draft?.platform || "instagram");
  const [artistOverride, setArtistOverride] = useState(null);

  // Let the user override the displayed name/handle for the mock.
  useEffect(() => {
    if (!open) return;
    try {
      const saved = localStorage.getItem("alc_preview_identity");
      if (saved) setArtistOverride(JSON.parse(saved));
    } catch { /* ignore */ }
  }, [open]);

  if (!draft) return null;

  const artist = {
    name: artistOverride?.name || user?.name || "Alchemist (CA)",
    handle: artistOverride?.handle || handleFromName(user?.name || "Alchemist (CA)"),
    initials: initialsFromName(artistOverride?.name || user?.name || "Alchemist (CA)"),
  };

  const saveIdentity = (next) => {
    setArtistOverride(next);
    try { localStorage.setItem("alc_preview_identity", JSON.stringify(next)); } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 rounded-sm w-[calc(100vw-1rem)] max-w-5xl text-white max-h-[95vh] overflow-y-auto p-4 sm:p-6"
        data-testid="draft-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">Preview</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-4 mb-3 text-white/70 label-mono">
          <div className="flex items-center gap-3">
            <span>Shown as</span>
            <input
              className="bg-transparent border-b border-white/20 focus:border-[#FF3B00] outline-none px-1 text-white text-sm font-sans min-w-[180px]"
              value={artist.name}
              onChange={(e) => saveIdentity({ name: e.target.value, handle: artist.handle })}
              data-testid="preview-identity-name"
            />
            <span className="text-white/30">@</span>
            <input
              className="bg-transparent border-b border-white/20 focus:border-[#FF3B00] outline-none px-1 text-white text-sm font-sans min-w-[120px]"
              value={artist.handle}
              onChange={(e) => saveIdentity({ name: artist.name, handle: e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, "") })}
              data-testid="preview-identity-handle"
            />
          </div>
          {artistOverride && (
            <button
              className="text-xs text-white/50 hover:text-white underline"
              onClick={() => { saveIdentity(null); localStorage.removeItem("alc_preview_identity"); }}
            >
              Reset
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-6 mt-2">
          {[
            { id: "instagram", label: "Instagram Reel" },
            { id: "facebook", label: "Facebook" },
            { id: "youtube", label: "YouTube Short" },
          ].map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              data-testid={`preview-tab-${v.id}`}
              className={`label-mono px-4 py-2 border transition-colors ${
                view === v.id
                  ? "border-[#FF3B00] text-white bg-[#FF3B00]/10"
                  : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
              }`}
            >
              {v.label}
            </button>
          ))}
          {view !== draft.platform && (
            <span className="label-mono text-white/40 px-2 py-2">
              Note: this draft is set to post on <span className="text-white">{draft.platform}</span>.
            </span>
          )}
        </div>

        <div className="py-6 flex items-center justify-center bg-gradient-to-br from-[#111] to-[#050505] rounded-sm min-h-[720px] overflow-hidden">
          <div className="origin-top scale-[0.7] sm:scale-[0.85] md:scale-100 transition-transform">
            {view === "instagram" && <InstagramReelMock draft={draft} artist={artist} />}
            {view === "facebook" && <FacebookPostMock draft={draft} artist={artist} />}
            {view === "youtube" && <YoutubeShortMock draft={draft} artist={artist} />}
          </div>
        </div>

        <div className="text-xs text-white/40 mt-4 leading-relaxed">
          Previews are approximations — final rendering on the actual platforms may vary (font weights, engagement
          counters, badge placement). Your real video, caption, hashtags{draft.audio_filename ? ", and attached audio" : ""} are shown.
        </div>
      </DialogContent>
    </Dialog>
  );
}
